/**
 * `/` — public landing page.
 *
 * Signed-out: hero + sign-in / sign-up CTAs.
 * Signed-in: redirect surface to the dashboard (the user normally lands here
 * only on a hard refresh of "/"; the router sends authenticated traffic to
 * "/dashboard" directly).
 */
import { SignedIn, SignedOut, useAuth, useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { fetchMe, type Me, resolveApiBaseUrl } from "@/lib/api-client";

const buildVersion = import.meta.env.VITE_BUILD_VERSION ?? "dev";

// RLY-278: must go through resolveApiBaseUrl (throws in prod when unset).
const apiBaseUrl = resolveApiBaseUrl({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  DEV: import.meta.env.DEV,
});

function SignedInPanel() {
  const { user } = useUser();
  const { getToken } = useAuth();

  const { data, error, isLoading } = useQuery<Me, Error>({
    queryKey: ["me"],
    queryFn: () => fetchMe(apiBaseUrl, () => getToken()),
  });

  return (
    <section
      aria-label="Signed-in welcome"
      className="flex flex-col items-center gap-4 text-center"
    >
      <p className="text-base text-gray-600">
        Welcome back,{" "}
        <span data-testid="welcome-first-name" className="font-semibold text-gray-900">
          {user?.firstName ?? user?.emailAddresses[0]?.emailAddress ?? "friend"}
        </span>
      </p>
      {isLoading && (
        <p data-testid="me-loading" className="text-sm text-muted-foreground">
          Loading workspace…
        </p>
      )}
      {error && (
        <p data-testid="me-error" className="text-sm text-destructive">
          Could not load workspace: {error.message}
        </p>
      )}
      {data && (
        <p data-testid="me-tenant-name" className="text-sm text-muted-foreground">
          Workspace:{" "}
          <span className="font-medium text-gray-700">{data.tenant.name}</span>
        </p>
      )}
      <div className="flex items-center gap-3">
        <Link to="/dashboard">
          <Button data-testid="dashboard-cta" className="rounded-full px-6">
            Go to dashboard
          </Button>
        </Link>
        <Link to="/sign-out">
          <Button data-testid="sign-out-cta" variant="outline" className="rounded-full px-6">
            Sign out
          </Button>
        </Link>
      </div>
    </section>
  );
}

function SignedOutPanel() {
  return (
    <section aria-label="Signed-out welcome" className="flex flex-row items-center gap-3">
      <Link to="/sign-in">
        <Button data-testid="sign-in-cta" className="rounded-full px-6">
          Sign in
        </Button>
      </Link>
      <Link to="/sign-up">
        <Button data-testid="sign-up-cta" variant="outline" className="rounded-full px-6">
          Get started
        </Button>
      </Link>
    </section>
  );
}

export function App() {
  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-white p-8 text-foreground">
      {/* Atmospheric background */}
      <div className="pointer-events-none absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full bg-blue-50 opacity-60" />
      <div className="pointer-events-none absolute -bottom-60 -left-40 h-[500px] w-[500px] rounded-full bg-amber-50/60" />
      <div className="pointer-events-none absolute right-1/3 top-1/2 h-80 w-80 rounded-full bg-rose-50/40 blur-3xl" />

      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Brand mark */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-[0_0_32px_rgba(37,99,235,0.35)]">
            <svg
              aria-hidden="true"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-4xl font-extrabold tracking-tight text-gray-900">
              Relay
            </span>
            <span className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-blue-600">
              by SimpliWork
            </span>
          </div>
        </div>

        {/* Headline */}
        <div className="max-w-lg text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
            Agentic AI Orchestration
          </p>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
            Orchestrate AI agents across your business
          </h1>
          <p className="mt-3 text-base leading-relaxed text-gray-500">
            Configure, deploy, and monitor AI agents from one place — with full
            visibility into every run.
          </p>
        </div>

        {/* Auth panels */}
        <SignedIn>
          <SignedInPanel />
        </SignedIn>
        <SignedOut>
          <SignedOutPanel />
        </SignedOut>
      </div>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 border-t border-white/[0.07] bg-gray-950 py-4">
        <p className="text-center text-xs text-white/30">
          Relay &middot; by SimpliWork &middot; build {buildVersion}
        </p>
      </footer>
    </main>
  );
}

export default App;
